import type { PropsWithChildren } from 'react';
import { Suspense, useMemo, useState } from 'react';

import type { Context } from './context';
import { SuspenseContext } from './context';
import Fallback from './Fallback';

interface AppSuspenseProps extends PropsWithChildren<unknown> {
  /**
   * A fallback react tree to show when a Suspense child (like React.lazy) suspends.
   */
  fallback?: JSX.Element;
}

/**
 * Suspense apparently renders a suspended tree before it resumes,
 * hiding it with `display: none;`.  This tweaked version lets child
 * components use the `useSuspenseContext` hook to know if they're in a
 * rendered-while-suspended tree.
 *
 * Source: https://github.com/facebook/react/issues/14536#issuecomment-602138550
 * @param props - component's props
 * @param props.fallback - A fallback react tree to show when a Suspense child (like React.lazy) suspends
 * @param props.children - child elements
 * @returns JSX.Element
 */
export const AppSuspense = ({ fallback, children }: AppSuspenseProps) => {
  const [suspended, setSuspended] = useState(true);

  const contextValue = useMemo<Context>(
    () => ({
      suspended,
    }),
    [suspended],
  );

  return (
    <Suspense fallback={<Fallback setSuspended={setSuspended}>{fallback}</Fallback>}>
      <SuspenseContext.Provider value={contextValue}>{children}</SuspenseContext.Provider>
    </Suspense>
  );
};
